import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Session, User } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { AUTH_STORAGE_KEY, supabase } from '@/services/supabaseClient';
import { authService } from '@/services/authService';
import { geolocationService } from '@/services/geolocationService';
import { subscriptionService } from '@/services/subscriptionService';
import { FREE_SHARED_SLOT_LIMIT } from '@/services/slotLimitService';
import { APP_ADMIN_EMAIL } from '@/utils/appAdmin';
import type { Profile, UserPlan } from '@/types/database';

export const useUserStore = defineStore('user', () => {
  type LocationSource = 'gps' | 'address' | null;
  type ProductLocationResolution = {
    status: 'ready' | 'blocked';
    source: LocationSource;
    // true quando la posizione è stata risolta SUBITO dall'indirizzo salvato e il
    // GPS non è ancora stato tentato: il chiamante (MapView) può fare un upgrade
    // al GPS in background e, se attivo, dargli la priorità.
    gpsPending?: boolean;
  };

  const user = ref<User | null>(null);
  const session = ref<Session | null>(null);
  const profile = ref<Profile | null>(null);
  const plan = ref<UserPlan | null>(null);
  const loading = ref(false);
  const locationSource = ref<LocationSource>(null);
  // Recupero password in corso: il link email ha creato la sessione di recovery
  // (evento PASSWORD_RECOVERY). Il guard del router usa questo flag per forzare
  // SEMPRE la vista /auth/reset-password, anche se l'evento scatta prima che il
  // router sia pronto (bootstrap) o se l'hash dell'URL è già stato consumato.
  const passwordRecovery = ref(false);
  let authChangeUnsubscribe: (() => void) | null = null;
  let subscriptionUpdatedListenerBound = false;
  let productLocationResolvePromise: Promise<ProductLocationResolution> | null = null;

  const isAuthenticated = computed(() => !!user.value);
  const isPro = computed(() => plan.value?.tier === 'pro');
  // Profilo verificato manualmente (negozi/associazioni): abilita tornei e banner.
  const isVerified = computed(() => profile.value?.is_verified === true);
  // Admin/creatore dell'app: unico gestore di banner e verifiche (email fissa).
  const isAdmin = computed(() =>
    String((user.value as { email?: string } | null)?.email ?? '').toLowerCase() === APP_ADMIN_EMAIL
  );
  const inventoryLimit = computed(() => {
    if (!user.value || !plan.value) return null;
    if (isPro.value) return null;
    const planLimit = plan.value?.inventory_slots;
    if (planLimit == null) return FREE_SHARED_SLOT_LIMIT;
    return Math.min(Math.max(1, Math.trunc(Number(planLimit))), FREE_SHARED_SLOT_LIMIT);
  });
  const maxRadiusKm = computed(() => plan.value?.max_radius_km ?? 25);
  const hasDefaultAddress = computed(() => hasProfileDefaultAddress(profile.value));

  type InitOptions = {
    blockingUserData?: boolean;
  };

  function extractSessionFromStoredPayload(raw: string | null): Session | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const directUser = parsed?.user as Record<string, unknown> | undefined;
      if (directUser?.id) return parsed as unknown as Session;

      // Backward/alternate payload shape used by some auth-js flows.
      const currentSession = parsed?.currentSession as Record<string, unknown> | undefined;
      const currentUser = currentSession?.user as Record<string, unknown> | undefined;
      if (currentUser?.id) return currentSession as unknown as Session;
    } catch {
      return null;
    }
    return null;
  }

  function hydrateSession() {
    // Read the cached session directly from localStorage — never call
    // supabase.auth.getSession() here. That call acquires the auth-js v2
    // internal lock; if it runs slowly (cold start, PKCE exchange, token
    // refresh) the lock is held and any subsequent signInWithPassword call
    // blocks indefinitely, making the login button appear stuck.
    // The INITIAL_SESSION event from onAuthStateChange (registered below,
    // before this runs) will update session/user with the validated token
    // once the Supabase client finishes its own async initialization.
    try {
      const restoredSession = extractSessionFromStoredPayload(
        window.localStorage.getItem(AUTH_STORAGE_KEY)
      );
      if (restoredSession?.user?.id) {
        session.value = restoredSession as Session;
        user.value = restoredSession.user as User;
      }
    } catch {
      // Storage corrupt or unavailable — stay logged out
    }
  }

  async function preloadUserData() {
    if (!user.value) {
      profile.value = null;
      plan.value = null;
      locationSource.value = null;
      return;
    }
    console.debug('[preloadUserData] caricamento dati utente', user.value.id);
    try {
      await Promise.all([fetchProfile(), fetchPlan()]);
      console.debug('[preloadUserData] completato — profile:', !!profile.value, 'plan:', plan.value?.tier ?? 'null');
    } catch (err) {
      console.error('[preloadUserData] fallito:', err);
      throw err;
    }
  }

  async function init(options: InitOptions = {}) {
    const { blockingUserData = true } = options;
    if (loading.value) return;
    loading.value = true;
    try {
      // Register the auth change listener BEFORE hydrating so the
      // INITIAL_SESSION event is never missed if the Supabase client
      // initializes before the first await below.
      if (!authChangeUnsubscribe) {
        const { data: { subscription } } = authService.onAuthChange((event, newSession: any) => {
          // Link di recupero password: si alza il flag così il guard porta alla
          // schermata di nuova password (robusto al timing di bootstrap/hash).
          if (event === 'PASSWORD_RECOVERY') passwordRecovery.value = true;
          const prevUserId = user.value?.id ?? null;
          session.value = newSession;
          user.value = newSession?.user ?? null;
          if (!user.value) {
            profile.value = null;
            plan.value = null;
            locationSource.value = null;
          } else if (user.value.id !== prevUserId) {
            // User ID changed: new sign-in or INITIAL_SESSION restoring a session
            // that hydrateSession() missed. Schedule outside the auth lock scope.
            // TOKEN_REFRESHED / USER_UPDATED keep the same ID → no redundant calls.
            setTimeout(() => {
              void preloadUserData().catch((err) => {
                console.error('Error loading user data after auth change:', err);
              });
            }, 0);
          }
        });
        authChangeUnsubscribe = () => subscription.unsubscribe();
      }

      // Dopo un acquisto/verifica abbonamento (evento emesso da subscriptionService
      // al termine della verify Play Billing) si ricarica il piano così isPro si
      // aggiorna immediatamente, senza dover riavviare l'app.
      if (!subscriptionUpdatedListenerBound && typeof window !== 'undefined') {
        window.addEventListener('swapwish:subscription-updated', () => {
          if (!user.value) return;
          void fetchPlan().catch(() => { });
          void fetchProfile().catch(() => { });
        });
        subscriptionUpdatedListenerBound = true;
      }

      hydrateSession();

      // Login persistente su mobile: se la WebView ha perso il localStorage
      // (eviction/aggiornamenti), la sessione vive ancora nelle preferenze
      // NATIVE (sopravvivono fino alla disinstallazione) — ripristinala.
      // Lettura diretta dallo storage: nessun lock auth-js coinvolto.
      if (!user.value && Capacitor.isNativePlatform()) {
        try {
          const { value } = await Preferences.get({ key: AUTH_STORAGE_KEY });
          if (value) {
            window.localStorage.setItem(AUTH_STORAGE_KEY, value);
            const restored = extractSessionFromStoredPayload(value);
            if (restored?.user?.id) {
              session.value = restored as Session;
              user.value = restored.user as User;
            }
          }
        } catch (error) {
          console.warn('[useUserStore] native session restore failed', error);
        }
      }

      if (user.value) {
        const preloadPromise = preloadUserData();
        if (blockingUserData) {
          await preloadPromise;
        } else {
          void preloadPromise.catch((err) => {
            console.error('Error preloading user data:', err);
          });
        }
      }
    } finally {
      loading.value = false;
    }
  }

  // Un solo retry ritardato per i fetch "di stato" (profilo/piano): molti
  // chiamanti li silenziano (.catch(() => {})) e un fallimento transitorio
  // lasciava profilo o piano stantii (es. utente ancora "Free" a schermo dopo
  // l'acquisto Pro) senza alcun recupero.
  async function withOneRetry<T>(run: () => Promise<T>, delayMs = 2000): Promise<T> {
    try {
      return await run();
    } catch (firstError) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      try {
        return await run();
      } catch {
        throw firstError;
      }
    }
  }

  async function fetchProfile() {
    if (!user.value) return;
    const data = await withOneRetry(async () => {
      const { data: row, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.value!.id)
        .maybeSingle();
      if (error) {
        console.error('[fetchProfile] errore query profiles:', error);
        throw error;
      }
      return row;
    });
    if (!data) {
      console.warn('[fetchProfile] nessun profilo trovato per', user.value.id);
      return;
    }
    profile.value = data as Profile;
  }

  async function fetchPlan() {
    if (!user.value) return;
    try {
      plan.value = await withOneRetry(() => subscriptionService.myPlan(user.value!.id));
    } catch (err) {
      console.error('[fetchPlan] errore query v_user_plan:', err);
      throw err;
    }
  }

  async function updateProfile(patch: Partial<Profile>) {
    if (!user.value) throw new Error('Non autenticato');
    const { data, error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', user.value.id)
      .select()
      .single();
    if (error) throw error;
    profile.value = data as Profile;
  }

  function normalizeAddressText(value?: string | null): string {
    return String(value ?? '').trim();
  }

  function hasProfileDefaultAddress(target?: Profile | null): boolean {
    if (!target) return false;
    // Un indirizzo "indicato" dall'utente nelle impostazioni ha sempre almeno la
    // via o la città/regione. postal_code e country_code da soli NON bastano:
    // vengono popolati anche dal reverse-geocoding del GPS (saveMyLocation) o da
    // un default di registrazione, quindi NON indicano un indirizzo compilato.
    // Senza questo filtro il banner blu "uso indirizzo del profilo" comparirebbe
    // anche senza indirizzo (al posto del banner giallo).
    return [
      target.street_address,
      target.city,
      target.region,
    ].some((part) => normalizeAddressText(part).length > 0);
  }

  function buildProfileAddressQuery(target: Profile): string {
    return [
      target.street_address,
      target.city,
      target.region,
      target.postal_code,
      target.country_code,
    ]
      .map(normalizeAddressText)
      .filter(Boolean)
      .join(', ');
  }

  function finiteCoordinate(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  async function refreshLocation() {
    const resolved = await resolveLocationForProducts();
    if (resolved.status !== 'ready') {
      throw new Error('location_unavailable');
    }
  }

  async function saveAddressLocation(lat: number, lon: number, postalCode?: string, countryCode?: string) {
    await geolocationService.saveMyLocation(lat, lon, postalCode, countryCode, 'address');
    locationSource.value = 'address';
    try {
      await updateProfile({
        address_latitude: lat,
        address_longitude: lon,
      });
    } catch (error) {
      console.warn('[saveAddressLocation] address coordinate columns unavailable', error);
    }
    await fetchProfile();
  }

  async function applyProfileAddressLocation(): Promise<boolean> {
    const currentProfile = profile.value;
    if (!currentProfile || !hasProfileDefaultAddress(currentProfile)) return false;

    const savedLat = finiteCoordinate(currentProfile.address_latitude);
    const savedLon = finiteCoordinate(currentProfile.address_longitude);
    if (savedLat != null && savedLon != null) {
      await geolocationService.saveMyLocation(
        savedLat,
        savedLon,
        currentProfile.postal_code ?? undefined,
        currentProfile.country_code ?? undefined,
        'address',
      );
      locationSource.value = 'address';
      await fetchProfile().catch(() => { });
      return true;
    }

    const addressQuery = buildProfileAddressQuery(currentProfile);
    if (!addressQuery) return false;
    const geocoded = await geolocationService.geocodeAddress(addressQuery);
    if (!geocoded) return false;
    await saveAddressLocation(
      geocoded.lat,
      geocoded.lon,
      currentProfile.postal_code ?? undefined,
      currentProfile.country_code ?? undefined,
    );
    return true;
  }

  // Upgrade al GPS in background, chiamato dopo una risoluzione via indirizzo:
  // se il dispositivo ha il GPS realmente attivo (non un fix stantìo), la
  // posizione viene sostituita con quella GPS — che ha la priorità — e persistita.
  // Restituisce true se il GPS è stato acquisito, false se non disponibile
  // (in tal caso resta l'indirizzo). Non blocca mai la UI: è best-effort.
  async function upgradeToGpsIfAvailable(): Promise<boolean> {
    try {
      await geolocationService.refreshAndSave();
      locationSource.value = 'gps';
      await fetchProfile().catch(() => { });
      return true;
    } catch (gpsError) {
      console.warn('[upgradeToGpsIfAvailable] GPS non disponibile, resta l\'indirizzo', gpsError);
      return false;
    }
  }

  async function resolveLocationForProducts(): Promise<ProductLocationResolution> {
    if (!user.value) {
      locationSource.value = null;
      return { status: 'blocked', source: null };
    }

    if (productLocationResolvePromise) return productLocationResolvePromise;

    const resolvePromise: Promise<ProductLocationResolution> = (async (): Promise<ProductLocationResolution> => {
      if (!profile.value) {
        await fetchProfile().catch(() => { });
      }

      // Se l'utente ha indicato un indirizzo nelle impostazioni, questo è la
      // sorgente PRIMARIA: si risolve SUBITO (coordinate salvate o, in mancanza,
      // un geocoding una tantum che poi le persiste), in modo deterministico e
      // senza attendere/dipendere dal GPS. In precedenza l'indirizzo era solo un
      // fallback del GPS: con permessi concessi ma GPS spento sul dispositivo, un
      // fix stantìo o il timeout del GPS potevano lasciare la mappa "bloccata"
      // ignorando l'indirizzo. Il GPS viene comunque tentato in background dal
      // chiamante (MapView) e, se attivo, ha la priorità (gpsPending: true).
      if (hasProfileDefaultAddress(profile.value) && await applyProfileAddressLocation()) {
        locationSource.value = 'address';
        return { status: 'ready', source: 'address', gpsPending: true };
      }

      try {
        // Nessun indirizzo salvato: il GPS resta la sorgente primaria.
        await geolocationService.refreshAndSave();
        locationSource.value = 'gps';
        await fetchProfile().catch(() => { });
        return { status: 'ready', source: 'gps' };
      } catch (gpsError) {
        console.warn('[resolveLocationForProducts] GPS unavailable, trying profile address fallback', gpsError);
      }

      await fetchProfile().catch(() => { });
      if (await applyProfileAddressLocation()) {
        locationSource.value = 'address';
        return { status: 'ready', source: 'address' };
      }

      locationSource.value = null;
      return { status: 'blocked', source: null };
    })();

    productLocationResolvePromise = resolvePromise.finally(() => {
      productLocationResolvePromise = null;
    });

    return resolvePromise;
  }

  async function signIn(email: string, password: string) {
    const data = await authService.signIn(email, password);
    // signInWithPassword() holds the auth lock for its entire scope and fires
    // onAuthStateChange synchronously before returning. The callback above detects
    // the new user ID and schedules preloadUserData() via setTimeout — outside the
    // lock scope. We set session/user here too for immediate reactivity, in case
    // the callback's reactive update hasn't propagated yet.
    session.value = data.session;
    user.value = data.user;
    // preloadUserData() is triggered by the onAuthStateChange callback; no second
    // call needed here.
  }

  /** Pre-check disponibilità username (vedi authService.isUsernameAvailable). */
  async function isUsernameAvailable(username: string): Promise<boolean> {
    return authService.isUsernameAvailable(username);
  }

  async function signUp(
    email: string,
    password: string,
    username: string,
    extras?: {
      first_name?: string;
      last_name?: string;
      street_address?: string;
      city?: string;
      region?: string;
      postal_code?: string;
      country_code?: string;
    }
  ) {
    await authService.signUp({ email, password, username, ...extras });
  }

  async function signInWithGoogle() {
    const data = await authService.signInWithGoogle();
    // Mobile (signInWithIdToken): la sessione è già pronta → si imposta subito
    // l'utente così il redirect a /tabs/map non aspetta onAuthChange.
    // Web (signInWithOAuth): redirect del browser, sessione via onAuthChange.
    const sess = (data as { session?: { user?: unknown } } | null)?.session as Session | null | undefined;
    if (sess?.user) {
      session.value = sess;
      user.value = sess.user as User;
      // Caso REGISTRAZIONE (utente nuovo creato ora da signInWithIdToken): il
      // trigger ha appena creato il profilo → lo si carica subito così le viste
      // post-login lo trovano. Best-effort: un fallimento transitorio non blocca
      // l'accesso (il profilo viene comunque ricaricato altrove).
      await fetchProfile().catch(() => { });
    }
  }

  async function resetPassword(email: string) {
    await authService.resetPassword(email);
  }

  async function updatePassword(newPassword: string) {
    await authService.updatePassword(newPassword);
  }

  async function signOut() {
    await authService.signOut();
    user.value = null;
    profile.value = null;
    plan.value = null;
    session.value = null;
    locationSource.value = null;
  }

  async function deleteAccount() {
    await authService.deleteAccount();
    user.value = null;
    profile.value = null;
    plan.value = null;
    session.value = null;
    locationSource.value = null;
  }

  return {
    user, session, profile, plan, loading, locationSource, passwordRecovery,
    isAuthenticated, isPro, isVerified, isAdmin, inventoryLimit, maxRadiusKm, hasDefaultAddress,
    init, fetchProfile, fetchPlan, updateProfile, refreshLocation, saveAddressLocation, resolveLocationForProducts,
    upgradeToGpsIfAvailable,
    signIn, signUp, signInWithGoogle, resetPassword, updatePassword, signOut, deleteAccount,
    isUsernameAvailable,
  };
});
